import { Link } from "react-router-dom";
import Footer from "../Footer";

/**
 * CoconeuseBioTemplate — Template harmonisé pour les pages bio des Coconeuses.
 * Props:
 *   name         — Prénom affiché (ex: "Valérie")
 *   subtitle     — Sous-titre en italique (ex: "Yoga sensible & vibrant")
 *   mainPhoto    — URL de la photo portrait principale
 *   photoPosition — CSS object-position pour la photo portrait (optionnel)
 *   bioText      — Tableau de paragraphes [ "paragraphe 1", "paragraphe 2", ... ]
 *   bioPdf       — URL du PDF bio (optionnel, affiché si fourni)
 *   galleryPhotos — Tableau d'URLs pour la galerie (optionnel)
 *   galleryStyles — Tableau de styles inline optionnels pour chaque photo galerie
 *   expertise    — Tableau de strings (domaines d'expertise)
 *   backPath     — Route du bouton retour (défaut "/#coconeuses-home")
 *   backLabel    — Label du bouton retour (défaut "Retour aux Coconeuses")
 */
const CoconeuseBioTemplate = ({
  name,
  subtitle,
  mainPhoto,
  photoPosition,
  bioText = [],
  bioPdf,
  galleryPhotos = [],
  galleryStyles = [],
  expertise = [],
  backPath = "/#coconeuses-home",
  backLabel = "Retour aux Coconeuses",
}) => {
  const galleryCols = galleryPhotos.length >= 3 ? "sm:grid-cols-3" : "sm:grid-cols-2";
  const galleryMaxW = galleryPhotos.length >= 3 ? "" : "max-w-lg mx-auto";

  return (
    <div className="min-h-screen bg-[#77574C] text-[#F3E5CF]" data-testid={`bio-page-${name.toLowerCase().replace(/\s/g, '-')}`}>
      {/* SECTION PORTRAIT + BIO */}
      <section className="px-6 pt-20 pb-16">
        <div className="max-w-2xl mx-auto text-center space-y-8">
          <p className="text-[10px] sm:text-xs tracking-[0.3em] uppercase text-[#C5A059]">Le cercle</p>
          <div className="flex justify-center">
            <div className="w-40 h-40 md:w-48 md:h-48 rounded-full overflow-hidden border-2 border-[#C5A059]">
              <img
                src={mainPhoto}
                alt={name}
                className="w-full h-full object-cover"
                style={photoPosition ? { objectPosition: photoPosition } : undefined}
                data-testid="bio-main-photo"
              />
            </div>
          </div>
          <h1 className="font-serif text-2xl md:text-3xl text-[#F3E5CF]" data-testid="bio-name">{name}</h1>
          <p className="font-serif text-sm text-[#F3E5CF]/60 italic">{subtitle}</p>
          <div className="w-12 h-[1px] bg-[#C5A059]/40 mx-auto" />
          <div className="space-y-5 text-sm sm:text-base text-[#F3E5CF]/80 leading-[2.2] max-w-xl mx-auto text-left">
            {bioText.map((paragraph, i) => (
              <p key={i}>{paragraph}</p>
            ))}
          </div>
          {bioPdf && (
            <a
              href={bioPdf}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-8 py-3 rounded-full border border-[#C5A059]/40 text-[#C5A059] text-[0.6rem] tracking-[0.18em] uppercase hover:bg-[#C5A059]/10 transition-all duration-500 min-h-[44px]"
              data-testid="bio-pdf-link"
            >
              Ouvrir la bio complète en PDF
            </a>
          )}
        </div>
      </section>

      {/* GALERIE PHOTOS */}
      {galleryPhotos.length > 0 && (
        <section className="px-6 pb-12">
          <div className="max-w-3xl mx-auto space-y-6">
            <h2 className="font-serif text-lg md:text-xl text-[#F3E5CF]/80 text-center">
              Quelques instantanés avec {name.split(' · ')[0]}
            </h2>
            <div className={`grid grid-cols-1 ${galleryCols} gap-4 ${galleryMaxW}`}>
              {galleryPhotos.map((url, i) => (
                <div key={i} className="aspect-[4/5] rounded-2xl overflow-hidden border border-[#EAD3AE]/30">
                  <img
                    src={url}
                    alt=""
                    className="w-full h-full object-cover"
                    style={galleryStyles[i] || undefined}
                    data-testid={`bio-gallery-photo-${i}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* EXPERTISE */}
      {expertise.length > 0 && (
        <section className="px-6 pb-16">
          <div className="max-w-2xl mx-auto text-center space-y-6">
            <p className="text-[10px] sm:text-xs tracking-[0.3em] uppercase text-[#C5A059]">Domaines d'expertise</p>
            <div className="flex flex-wrap justify-center gap-3">
              {expertise.map((e, i) => (
                <span key={i} className="px-5 py-2 rounded-full border border-[#F3E5CF]/20 text-xs tracking-[0.15em] text-[#F3E5CF]/70">
                  {e}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* BOUTON RETOUR */}
      <div className="px-6 pb-12 flex justify-center">
        <Link
          to={backPath}
          className="inline-flex items-center px-8 py-3 rounded-full border border-[#F3E5CF]/30 text-[#F3E5CF] text-[0.6rem] tracking-[0.18em] uppercase hover:bg-[#F3E5CF]/10 transition-all duration-500 min-h-[44px]"
          data-testid="back-to-coconeuses"
        >
          {backLabel}
        </Link>
      </div>

      <Footer />
    </div>
  );
};

export default CoconeuseBioTemplate;
